"use client";

import type { ReactNode } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useEventTypeById } from "@calcom/atoms/hooks/event-types/private/useEventTypeById";
import { useIsPlatform } from "@calcom/atoms/hooks/useIsPlatform";
import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import { Dialog } from "@calcom/features/components/controlled-dialog";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Badge } from "@calcom/ui/components/badge";
import { DialogContent } from "@calcom/ui/components/dialog";

import { getDurationFormatted } from "../event-meta/Duration";
import { FromTime } from "@calcom/features/bookings/Booker/utils/dates";
import { useEvent } from "@calcom/web/modules/schedules/hooks/useEvent";
import { useBookerTime } from "@calcom/features/bookings/Booker/hooks/useBookerTime";

const BookEventFormWrapper = ({ children, onCancel }: { onCancel: () => void; children: ReactNode }) => {
  const { data } = useEvent();

  return <BookEventFormWrapperComponent child={children} eventLength={data?.length} onCancel={onCancel} />;
};

const PlatformBookEventFormWrapper = ({
  children,
  onCancel,
}: {
  onCancel: () => void;
  children: ReactNode;
}) => {
  const eventId = useBookerStoreContext((state) => state.eventId);
  const { data } = useEventTypeById(eventId);

  return (
    <BookEventFormWrapperComponent child={children} eventLength={data?.lengthInMinutes} onCancel={onCancel} />
  );
};

export const BookEventFormWrapperComponent = ({
  child,
  eventLength,
}: {
  onCancel: () => void;
  child: ReactNode;
  eventLength?: number;
}) => {
  const { i18n, t } = useLocale();
  const selectedTimeslot = useBookerStoreContext((state) => state.selectedTimeslot);
  const selectedDuration = useBookerStoreContext((state) => state.selectedDuration);
  const recurringEventCount = useBookerStoreContext((state) => state.recurringEventCount);
  const { timeFormat, timezone } = useBookerTime();
  if (!selectedTimeslot) {
    return null;
  }
  return (
    <>
      <div className="knopka-confirm-drag-surface">
        <h1 className="font-cal text-emphasis shrink-0 text-xl leading-5">{t("confirm_your_details")} </h1>
        <div className="my-4 flex shrink-0 flex-wrap gap-2 rounded-md leading-none">
          <Badge variant="grayWithoutHover" startIcon="calendar" size="lg">
            <FromTime
              date={selectedTimeslot}
              timeFormat={timeFormat}
              timeZone={timezone}
              language={i18n.language}
            />
          </Badge>
          {(selectedDuration || eventLength) && (
            <Badge variant="grayWithoutHover" startIcon="clock" size="lg">
              <span>{getDurationFormatted(selectedDuration || eventLength, t)}</span>
            </Badge>
          )}

          {recurringEventCount && recurringEventCount > 1 && (
            <Badge variant="grayWithoutHover" startIcon="refresh-ccw" size="lg">
              <span>
                {t("repeats_num_times", {
                  count: recurringEventCount,
                })}
              </span>
            </Badge>
          )}
        </div>
      </div>
      {child}
    </>
  );
};

function useHideKeyboardButton(enabled: boolean) {
  const [visible, setVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const getModalSurface = useCallback(() => {
    const el = modalRef.current;
    if (!el) return null;
    return (el.closest(".knopka-confirm-modal") as HTMLElement | null) || el;
  }, []);

  const updateInset = useCallback(() => {
    const el = modalRef.current;
    const surface = getModalSurface();
    if (!el || !surface || typeof window === "undefined") return;
    const vv = window.visualViewport;
    const inset = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
    surface.style.setProperty("--knopka-confirm-keyboard-inset", `${Math.round(inset)}px`);

    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName?.toLowerCase();
    const isEditable =
      !!active &&
      (tag === "input" || tag === "textarea" || active.isContentEditable === true) &&
      el.contains(active);
    setVisible(isEditable || inset > 80);
  }, [getModalSurface]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const onFocusIn = () => updateInset();
    const onFocusOut = () => {
      window.setTimeout(updateInset, 50);
    };
    const vv = window.visualViewport;
    vv?.addEventListener("resize", updateInset);
    vv?.addEventListener("scroll", updateInset);
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    updateInset();

    return () => {
      vv?.removeEventListener("resize", updateInset);
      vv?.removeEventListener("scroll", updateInset);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      getModalSurface()?.style.removeProperty("--knopka-confirm-keyboard-inset");
    };
  }, [enabled, updateInset, getModalSurface]);

  const hideKeyboard = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === "function") {
      active.blur();
    }
    setVisible(false);
    updateInset();
  }, [updateInset]);

  return { visible, hideKeyboard, modalRef, getModalSurface };
}

/** Finger drag for the centered confirm card on mobile only (header + grab handle). */
function useConfirmModalDrag(enabled: boolean, getSurface: () => HTMLElement | null) {
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const readOffset = (surface: HTMLElement) => {
    const x = Number.parseFloat(surface.style.getPropertyValue("--knopka-drag-x") || "0") || 0;
    const y = Number.parseFloat(surface.style.getPropertyValue("--knopka-drag-y") || "0") || 0;
    return { x, y };
  };

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const mobileMq = window.matchMedia("(max-width: 639px)");
    if (!mobileMq.matches) return;

    const isDragSource = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest(".knopka-confirm-drag-handle") || target.closest(".knopka-confirm-drag-surface")
      );
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!mobileMq.matches) return;
      if (e.button !== 0 && e.pointerType === "mouse") return;
      if (!isDragSource(e.target)) return;
      // Don't start drag from interactive controls inside the header
      if (e.target instanceof Element && e.target.closest("button, a, input, textarea, select")) return;

      const surface = getSurface();
      if (!surface || !surface.contains(e.target as Node)) return;
      const { x, y } = readOffset(surface);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: x,
        originY: y,
      };
      setDragging(true);
      surface.dataset.dragging = "true";
      if (e.target instanceof Element) {
        try {
          e.target.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      }
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      const surface = getSurface();
      if (!drag || !surface || drag.pointerId !== e.pointerId) return;
      const nextX = drag.originX + (e.clientX - drag.startX);
      const nextY = drag.originY + (e.clientY - drag.startY);
      // Soft clamp so the card can't fully leave the viewport
      const maxX = Math.round(window.innerWidth * 0.4);
      const maxY = Math.round(window.innerHeight * 0.4);
      const clampedX = Math.max(-maxX, Math.min(maxX, nextX));
      const clampedY = Math.max(-maxY, Math.min(maxY, nextY));
      surface.style.setProperty("--knopka-drag-x", `${Math.round(clampedX)}px`);
      surface.style.setProperty("--knopka-drag-y", `${Math.round(clampedY)}px`);
    };

    const endDrag = (e: PointerEvent) => {
      const drag = dragRef.current;
      const surface = getSurface();
      if (!drag || drag.pointerId !== e.pointerId) return;
      dragRef.current = null;
      setDragging(false);
      if (surface) {
        delete surface.dataset.dragging;
        try {
          surface.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      }
    };

    const onViewportChange = () => {
      if (mobileMq.matches) return;
      // Left mobile breakpoint — drop any in-progress drag offset
      dragRef.current = null;
      setDragging(false);
      const surface = getSurface();
      if (surface) {
        surface.style.removeProperty("--knopka-drag-x");
        surface.style.removeProperty("--knopka-drag-y");
        delete surface.dataset.dragging;
      }
    };

    document.addEventListener("pointerdown", onPointerDown, { passive: false });
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", endDrag);
    document.addEventListener("pointercancel", endDrag);
    mobileMq.addEventListener("change", onViewportChange);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", endDrag);
      document.removeEventListener("pointercancel", endDrag);
      mobileMq.removeEventListener("change", onViewportChange);
      const surface = getSurface();
      if (surface) {
        surface.style.removeProperty("--knopka-drag-x");
        surface.style.removeProperty("--knopka-drag-y");
        delete surface.dataset.dragging;
      }
      dragRef.current = null;
    };
  }, [enabled, getSurface]);

  return { dragging };
}

export const BookFormAsModal = ({
  visible,
  onCancel,
  children,
}: {
  visible: boolean;
  onCancel: () => void;
  children: ReactNode;
}) => {
  const isPlatform = useIsPlatform();
  const { t } = useLocale();
  const { visible: keyboardOpen, hideKeyboard, modalRef, getModalSurface } = useHideKeyboardButton(visible);
  const { dragging } = useConfirmModalDrag(visible, getModalSurface);

  return (
    <Dialog open={visible} onOpenChange={onCancel}>
      <DialogContent
        type={undefined}
        className="knopka-glass-surface knopka-confirm-modal flex max-h-[min(80vh,calc(100dvh-1.5rem))] max-sm:max-h-none flex-col overflow-hidden max-sm:overflow-visible px-8 pb-0 pt-8"
        data-dragging={dragging ? "true" : "false"}
        onOpenAutoFocus={(e) => {
          // Don't steal focus into Name/Email — opens mobile keyboard on timeslot pick.
          e.preventDefault();
        }}>
        <div ref={modalRef} className="flex min-h-0 flex-col overflow-hidden">
          <div
            className="knopka-confirm-drag-handle"
            data-testid="knopka-confirm-drag-handle"
            aria-hidden="true"
          />
          {!isPlatform ? (
            <BookEventFormWrapper onCancel={onCancel}>{children}</BookEventFormWrapper>
          ) : (
            <PlatformBookEventFormWrapper onCancel={onCancel}>{children}</PlatformBookEventFormWrapper>
          )}
          <button
            type="button"
            className="knopka-hide-keyboard-btn"
            data-visible={keyboardOpen ? "true" : "false"}
            data-testid="knopka-hide-keyboard"
            onClick={hideKeyboard}>
            {t("knopka_hide_keyboard")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
